import { ComponentType, memo, useEffect, useState } from 'react';
import styled from 'styled-components';

import { getViewerPromise } from '@vimeo/core/libs/viewer-promise';
import { AnyObject, Definable } from '@vimeo/core/types/generic';
import { useSetUpJwtPolling, Viewer, ViewerContext } from '@vimeo/viewer';

import Loading from '../lead-capture-form/preview/loading/Loading';

interface IWithViewerOptions {
  hideSpinner: boolean;
}

export const withViewer = <P extends AnyObject>(
  Component: ComponentType<P>,
  options: Definable<IWithViewerOptions> = { hideSpinner: false },
) => {
  return memo((props: P) => {
    const [viewer, setViewer] = useState<Viewer | null>(null);

    // token refresh
    useSetUpJwtPolling({
      viewer,
      onSuccess: (jwt) => setViewer((prev) => prev && { ...prev, jwt }),
    });

    useEffect(() => {
      getViewerPromise().then((_viewer) => {
        if (!_viewer) {
          return;
        }
        setViewer({ ..._viewer, setUser: (user) => setViewer((viewer) => ({ ...viewer!, user })) });
      });
    }, []);

    if (!viewer) {
      if (options.hideSpinner) {
        return null;
      }

      return (
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      );
    }

    return (
      <ViewerContext.Provider value={viewer}>
        <Component {...props} />
      </ViewerContext.Provider>
    );
  });
};

const LoadingWrapper = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100%;
  align-items: center;
  justify-content: center;
`;
